import { socket } from '@/api/socket';
import ClientAppCard from '@/components/client/client-app-card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import type { IClient } from '@/entities/client.entity';
import { $clientDetailApps, $setClientDetailApps } from '@/entities/client.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { getMockAppsForClient } from '@/mocks/fixtures';
import { useUnit } from 'effector-react';
import { LayoutGridIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface ClientAppsModalProps {
	client: IClient;
	onCopy: (text: string) => void;
}

export default function ClientAppsModal({ client, onCopy }: ClientAppsModalProps) {
	const [apps, setClientDetailApps] = useUnit([$clientDetailApps, $setClientDetailApps]);
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const fetchCleanupRef = useRef<(() => void) | null>(null);

	const appsCount = client.appsCount ?? 0;
	const isSearching = searchText.trim().length > 0;

	function cleanupFetch() {
		fetchCleanupRef.current?.();
		fetchCleanupRef.current = null;
	}

	function handleOpenChange(open: boolean) {
		cleanupFetch();
		setIsOpen(open);

		if (!open) {
			setSearchText('');
			setIsLoading(false);
			return;
		}

		setSearchText('');
		setIsLoading(true);

		if (isDevMode) {
			setClientDetailApps(getMockAppsForClient());
			setIsLoading(false);
			return;
		}

		setClientDetailApps([]);
		socket?.emit('get-client-apps', client.id);

		let settled = false;
		const finish = () => {
			if (settled) return;
			settled = true;
			setIsLoading(false);
			socket?.off('set-client-apps', onApps);
			window.clearTimeout(timeoutId);
		};

		const onApps = () => finish();
		const timeoutId = window.setTimeout(finish, 10000);
		socket?.on('set-client-apps', onApps);
		fetchCleanupRef.current = finish;
	}

	useEffect(() => () => cleanupFetch(), []);

	const filteredApps = useMemo(() => {
		const q = searchText.trim().toLowerCase();
		if (!q) return apps;
		return apps.filter(app => app.label.toLowerCase().includes(q) || app.packageName.toLowerCase().includes(q));
	}, [apps, searchText]);

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant='outline' size='sm' className='h-8 text-xs w-full'>
					<LayoutGridIcon size={14} />
					Приложения{appsCount > 0 ? ` (${appsCount})` : ''}
				</Button>
			</DialogTrigger>
			<DialogContent className='sm:max-w-[480px] w-full max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0'>
				<DialogHeader className='px-4 pt-4 pb-2 shrink-0'>
					<DialogTitle className='text-left'>
						Установленные приложения
						{appsCount > 0 ? (
							<span className='ml-1.5 text-muted-foreground font-normal tabular-nums'>· {appsCount}</span>
						) : null}
					</DialogTitle>
				</DialogHeader>

				<div className='px-4 pb-2 shrink-0 min-w-0'>
					<Input
						className='w-full min-w-0'
						placeholder='Поиск...'
						value={searchText}
						onChange={e => setSearchText(e.target.value)}
					/>
					{isSearching ? (
						<p className='mt-1.5 text-xs text-muted-foreground tabular-nums'>Найдено: {filteredApps.length}</p>
					) : null}
				</div>

				<div className='flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4'>
					{isLoading ? (
						<div className='flex items-center justify-center py-10'>
							<Spinner className='w-8 h-8' />
						</div>
					) : filteredApps.length ? (
						<div className='grid gap-0.5 min-w-0'>
							{filteredApps.map(app => (
								<ClientAppCard key={app.id} app={app} onCopy={onCopy} />
							))}
						</div>
					) : (
						<div className='flex items-center justify-center py-10 text-sm text-muted-foreground'>
							<span>{isSearching ? 'Ничего не найдено' : 'Приложения не получены'}</span>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
